import React from 'react';
import './FmTooltip.css';

const cx = (...parts: Array<string | false | null | undefined>): string =>
  parts.filter(Boolean).join(' ');

export type FmTooltipPlacement = 'top' | 'bottom' | 'left' | 'right';

export interface FmTooltipProps {
  /** Tooltip content. When empty/null the trigger renders with no tooltip. */
  title?: React.ReactNode;
  placement?: FmTooltipPlacement;
  /**
   * @internal DS-internal className prop; forbidden in route-owned code.
   */
  className?: string;
  children: React.ReactNode;
  'data-testid'?: string;
}

/**
 * Hover/focus tooltip — the design-system replacement for AntD `Tooltip`.
 * CSS-only (no portal): the trigger is wrapped in an inline span and the bubble
 * is shown on `:hover`/`:focus-within` for keyboard accessibility. Dark bubble
 * (ephemeral chrome) with token-driven spacing/typography.
 */
export const FmTooltip: React.FC<FmTooltipProps> = ({
  title,
  placement = 'top',
  className,
  children,
  ...rest
}) => {
  const bubbleId = React.useId();
  if (title === null || title === undefined || title === '') {
    // No tooltip content — render the trigger transparently.
    return children as React.ReactElement;
  }
  // Associate the trigger with the bubble for screen readers (aria-describedby).
  const describedChild = React.isValidElement(children)
    ? React.cloneElement(
        children as React.ReactElement<React.HTMLAttributes<HTMLElement>>,
        {
          'aria-describedby': bubbleId,
        }
      )
    : children;
  return (
    <span className={cx('fm-tooltip', className)} {...rest}>
      {describedChild}
      <span
        id={bubbleId}
        className={cx('fm-tooltip__bubble', `fm-tooltip__bubble--${placement}`)}
        role="tooltip"
      >
        {title}
      </span>
    </span>
  );
};

export default FmTooltip;
